import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Subject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import CanvasEditor from './CanvasEditor';
import CanvasEditorTabs from './CanvasEditorTabs';
import {CanvasMarkdown} from './CanvasMarkdown'
import './MultiFileCanvasEditor.css';

/**
 * MultiFileCanvasEditor component - Container that manages multiple editors
 * based on commands from the interpreter
 */
class MultiFileCanvasEditor extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      currentPath: null,
      openFiles: [],
      openWebPages: [],
    };
    
    // Create a subject for user edits
    this.editsSubject = new Subject();
    
    // Track editor refs
    this.editorRefs = new Map();
  }

  componentDidUpdate() {
    this.checkDesktopFiles()
  }

  componentDidMount() {
    this.init()
  }

  fileGets = {}

  checkDesktopFiles = () => {
    if (this.props.desktopFiles) {
      const files = Object.values(this.props.desktopFiles)
      console.log("DESKTOP FILES", files)
      for (const file of files) {
        const { path, timestamp, type } = file
        const fileContents = this.fileContents.get(path)
        if (fileContents) {
          console.log("FILE CONTENTS", path, timestamp, fileContents)
          const { ts, content } = fileContents
          if (timestamp > ts) {
            if (this.fileGets[path] && this.fileGets[path].lastUpdated >= ts) {
              fileContents.content = this.fileGets[path]
              fileContents.ts = this.fileGets.lastUpdated
            }
            if (false) this.props.getFileContents(path).then(file => {
              console.log("GOT FILE CONTENT", file)
              const oldContent = fileContents.content
              this.fileGets[path] = file
              if (oldContent !== file.content) {
                fileContents.content = file.content
                fileContents.ts = file.lastUpdated
                this.editsSubject.next({
                  path,
                  type: "file-change",
                  oldContent,
                  content: fileContents.content
                })
                this.forceUpdate()
              }
            }).finally(() => {

            })
          }
        }
      }
    }
  }

  init = () => {
    //debugger
    // Subscribe to commands
    this.commandsSubscription = this.props.commands$.subscribe(cmd => {
      //debugger
      this.handleCommand(cmd);
    });
    this.sub1 = this.editsSubject.subscribe(this.props.onUserEdit)
    // Subscribe to available files
    if (this.props.availableFiles$) {
      this.filesSubscription = this.props.availableFiles$.subscribe(files => {
        // If we have no open files but files are available, open the first one
        if (files.length > 0 && this.state.openFiles.length === 0) {
          this.handleFileOpen(files[0]);
        }
        
        // Remove any open files that no longer exist
        this.setState(prevState => {
          const validOpenFiles = prevState.openFiles.filter(path => 
            files.includes(path)
          );
          
          // If current file is no longer valid, switch to first valid file
          let newCurrentPath = prevState.currentPath;
          if (newCurrentPath && !files.includes(newCurrentPath)) {
            newCurrentPath = validOpenFiles[0] || null;
          }
          
          return {
            openFiles: validOpenFiles,
            currentPath: newCurrentPath
          };
        });
      });
    }
  }

  componentWillUnmount() {
    if (this.sub1) {
      this.sub1.unsubscribe()
      this.sub1 = null
    }
    // Clean up subscriptions
    if (this.commandsSubscription) {
      this.commandsSubscription.unsubscribe();
    }
    
    if (this.filesSubscription) {
      this.filesSubscription.unsubscribe();
    }
    
    // Complete the edits subject
    this.editsSubject.complete();
  }

  fileContents = new Map()
  webContents = new Map()
  /**
   * Handle a command from the interpreter
   * @param {Object} cmd - Command object
   */
  handleCommand(cmd) {
    switch (cmd.type) {
      case 'RESET':
        console.log("RESET MultiFileCanvasEditor")
        this.fileContents = new Map()
        break
      case 'CREATE_FILE':
      case 'UPDATE_FILE':
        // Store file content
        const newFileContents = cmd.content
        const old = this.fileContents.get(cmd.path)
        if (!old || Number(old.ts || 0) <= Number(cmd.ts || 0)) {
          this.fileContents.set(cmd.path, { content: cmd.content, ts: cmd.ts});
          if (cmd.path === this.state.currentPath && this.props.onFileVisible) {
            this.props.onFileVisible(cmd.path)
          }
          this.forceUpdate()
        }
        return { fileContents: newFileContents };
      case 'WEB_PAGE_NAVIGATE':
        this.webContents.set(cmd.target, cmd.url);
        this.forceUpdate()
        return { webContents: newWebContents };
    }
  }


  /**
   * Get the edits observable
   * @returns {Observable} - Observable of editor edits
   */
  getEdits$() {
    return this.editsSubject
  }

  /**
   * Register an editor reference
   * @param {string} path - File path
   * @param {CanvasEditor} ref - Editor reference
   */
  registerEditor(path, ref) {
    if (ref && !this.editorRefs.has(path)) {
      this.editorRefs.set(path, ref);
    }
  }

  /**
   * Unregister an editor reference
   * @param {string} path - File path
   */
  unregisterEditor(path) {
    const ref = this.editorRefs.get(path);
    if (ref) {
      // Clean up subscription
      if (ref._subscription) {
        ref._subscription.unsubscribe();
      }
      
      this.editorRefs.delete(path);
    }
  }

  /**
   * Handle file open
   * @param {string} path - File path
   */
  handleFileOpen = (path) => {
    if (this.props.onFileVisible) this.props.onFileVisible(path)
    this.setState(prevState => {
      // Already open?
      //debugger
      if (prevState.openFiles.includes(path)) {
        return { currentPath: path };
      }
      
      // Add to open files
      return {
        openFiles: [...prevState.openFiles, path],
        currentPath: path
      };
    });
  };

  /**
   * Handle tab change
   * @param {string} path - New active path
   */
  handleTabChange = (path) => {
    if (this.props.onFileVisible) this.props.onFileVisible(path)
    this.setState({ currentPath: path });
  };

  /**
   * Handle tab close
   * @param {string} path - Path to close
   */
  handleTabClose = (path) => {
    this.setState(prevState => {
      const newOpenFiles = prevState.openFiles.filter(p => p !== path);
      let newCurrentPath = prevState.currentPath;
      
      // If closing current path, switch to another tab
      if (path === prevState.currentPath) {
        const index = prevState.openFiles.indexOf(path);
        newCurrentPath = newOpenFiles[index] || newOpenFiles[0] || null;
      }
      
      return {
        openFiles: newOpenFiles,
        currentPath: newCurrentPath
      };
    });
    
    // Unregister editor
    this.unregisterEditor(path);
  };

  /**
   * Render a file editor
   * @param {string} path - File path
   * @returns {JSX.Element} - Editor component
   */
  renderFileEditor(path) {
    const { ts, content } = this.fileContents.get(path) || '';
    let style = path === this.state.currentPath ? null : { display: 'none' }
    return (
      <div className='canvasEditorContainer' style={style}>
        {path && path.toLowerCase().endsWith(".md")
         ?
         <CanvasMarkdown
          editsSubject={this.editsSubject}
          key={path}
          ref={ref => this.registerEditor(path, ref)}
          path={path}
          content={content}
         />
        :
        <CanvasEditor
          editsSubject={this.editsSubject}
          key={path}
          ref={ref => this.registerEditor(path, ref)}
          path={path}
          content={content}
        />}
      </div>
    );
  }

  render() {
    let { currentPath, openFiles } = this.state;
    openFiles = Array.from(this.fileContents.keys()).filter(key => this.fileContents.get(key))
    openFiles = openFiles.sort((x, y) => {
      const c1 = this.fileContents.get(x)
      const c2 = this.fileContents.get(y)
      const cmp = c2.ts - c1.ts
      if (cmp) return cmp
      return x.localeCompare(y)
    })
    console.log({openFiles})
    return (
      <div className="multi-file-canvas-editor">
        <div className="editor-container">
          <CanvasEditorTabs 
            filePaths={openFiles}
            activePath={currentPath}
            onTabChange={this.handleTabChange}
            onTabClose={this.handleTabClose}
            allFilePaths={this.props.availableFiles$.value || []}
            onFileOpen={this.handleFileOpen}
          />
          <div className="editor-wrapper">
            {
              openFiles.map(path => {
                return this.renderFileEditor(path)
              })
            }
            {!currentPath && (
              <div className="no-file-selected">
                <div className="no-file-message">No file selected</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

MultiFileCanvasEditor.propTypes = {
  commands$: PropTypes.object.isRequired, // Observable of editor commands
  availableFiles$: PropTypes.object // Observable of available files
};

export default MultiFileCanvasEditor;
